import React, { createContext, useContext, useState, useEffect } from 'react';
import { generateId } from '../utils/helpers';
import { useCRM } from './CRMContext';
import { mockAI } from '../services/mockAI';

const PlaybookContext = createContext();

export const usePlaybook = () => useContext(PlaybookContext);

export const PlaybookProvider = ({ children }) => {
  const { currentUser, addActivity, users } = useCRM();

  // --- STATE ---
  const [playbooks, setPlaybooks] = useState([]);
  const [partnerPlaybooks, setPartnerPlaybooks] = useState([]);
  const [marketplaceItems, setMarketplaceItems] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [battlecards, setBattlecards] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [scenarioUsage, setScenarioUsage] = useState([]);
  const [onboardingTasks, setOnboardingTasks] = useState([]);
  const [coachEvents, setCoachEvents] = useState([]);
  const [aiPolicies, setAiPolicies] = useState([]);
  const [governanceReports, setGovernanceReports] = useState([]);
  const [learningModules, setLearningModules] = useState([]);

  // --- MOCK INIT ---
  useEffect(() => {
    // Seed Marketplace Items
    if (marketplaceItems.length === 0) {
      setMarketplaceItems([
        {
          mpi_id: 'compliance_sales_kit_v1',
          title: 'Compliance Sales Kit',
          description: 'Et komplett, ferdig oppsett for salg av compliance- og HMS-relaterte tjenester. Inkluderer playbooks, microsites, AI-støtte, lead intelligence og styre-/lederoversikt. Bygget for GDPR og revisjon.',
          industry: 'Compliance / HMS',
          provider_type: 'Fugleberg',
          price_model: 'Abonnement',
          price: 1490,
          billing_period: 'monthly',
          includes: 'Compliance Oversikt microsite, ROI vs Risiko microsite, Compliance – Standard e-postsekvens, Lead Intelligence Starter Kit, HMS/Compliance Scenario Pack, Compliance Funnel Dashboard, Board / Ledelse Dashboard',
          verified: true,
          version: '1.0'
        },
        {
          mpi_id: 'mpi-1',
          title: 'SaaS Startup Kit',
          description: 'Komplett playbook for tidligfase B2B SaaS. Inkluderer discovery-manus og demo-struktur.',
          industry: 'SaaS',
          provider_type: 'Fugleberg',
          price_model: 'Gratis',
          price: 0,
          includes: '5 Scenarioer, 3 Battlecards',
          verified: true,
          version: '2.1'
        },
        {
          mpi_id: 'mpi-2',
          title: 'Enterprise Banking Sales',
          description: 'Avansert metodikk for salg til finanssektoren. Fokus på compliance og stakeholder mapping.',
          industry: 'Finans',
          provider_type: 'Partner',
          price_model: 'Engangskjøp',
          price: 4900,
          includes: '10 Scenarioer, 1 Onboarding Plan',
          verified: true,
          version: '1.0'
        },
        {
          mpi_id: 'mpi-3',
          title: 'Eiendomsmegling Pro',
          description: 'Best practice for næringsmegling. Fra befaring til budrunde.',
          industry: 'Eiendom',
          provider_type: 'Community',
          price_model: 'Abonnement',
          price: 299,
          includes: 'Full prosess',
          verified: false,
          version: '0.5-beta'
        }
      ]);
    }

    // Seed Partner Playbooks
    if (partnerPlaybooks.length === 0) {
      setPartnerPlaybooks([
        {
          ppb_id: 'ppb-mock-1',
          marketplace_item_id: 'mpi-1',
          name: 'Winning by Design - SaaS Method',
          industry: 'SaaS',
          locked: true,
          template_scenarios: [
            {
              name: "Impact Discovery",
              category: "Discovery",
              goal: "Uncover critical business impact.",
              checklist: "- Ask 'So what?'\n- Quantify pain",
              recommended_questions: "How does this affect your revenue?"
            },
            {
              name: "Trade-off Negotiation",
              category: "Pris/forhandling",
              goal: "Trade value, don't discount.",
              checklist: "- Identify give-gets\n- Lock scope",
              recommended_questions: "If we do X, can you sign by Y?"
            }
          ]
        }
      ]);
    }
    
    // Seed AI Policies
    if (aiPolicies.length === 0) {
      setAiPolicies([
        { id: 'pol-1', name: 'Human-in-the-loop', description: 'Alle AI-genererte e-poster må godkjennes av menneske før sending.', active: true },
        { id: 'pol-2', name: 'Data Privacy', description: 'Ingen PII brukes til å trene generelle modeller.', active: true }
      ]);
    }
  }, []);

  // --- ACTIONS ---

  const createLearningModule = (moduleData) => {
    const newModule = {
      plm_id: generateId('plm'),
      created_at: new Date().toISOString(),
      created_by: currentUser?.user_id,
      scorm_ready: true,
      status: 'Draft',
      ...moduleData
    };
    setLearningModules(prev => [newModule, ...prev]);
    addActivity({
      type: 'Oppgave',
      title: `Opprettet læringsmodul: ${newModule.title}`,
      description: `Basert på ${newModule.content_source}.`,
      project_id: newModule.project_id,
      status: 'Gjennomført',
      date: new Date().toISOString()
    });
  };

  const generateGovernanceReport = async () => {
    const newReport = {
      report_id: generateId('gov-rep'),
      tenant_id: currentUser?.organization_id || 'org-1',
      generated_at: new Date().toISOString(),
      generated_by: currentUser?.user_id,
      scope: 'Full Sales Audit',
      methodology: `Active Playbooks: ${partnerPlaybooks.length}`,
      roles_and_responsibility: users.map(u => `- ${u.name}: ${u.role}`).join('\n'),
      ai_usage_policy: aiPolicies.filter(p => p.active).map(p => `- ${p.name}: ${p.description}`).join('\n'),
      onboarding_process: `Standard Tasks: ${onboardingTasks.length}`,
      scenario_management: scenarios.map(s => `- ${s.name}`).join('\n'),
      audit_traceability: `System logs active. ISO 27001 Compliant.`
    };
    setGovernanceReports(prev => [newReport, ...prev]);
    return newReport;
  };

  const purchaseMarketplaceItem = (mpiId) => {
    const item = marketplaceItems.find(i => i.mpi_id === mpiId);
    if (!item) return;
    const newPurchase = {
      purchase_id: generateId('pur'),
      mpi_id: mpiId,
      tenant_id: currentUser?.organization_id || 'org-1',
      purchased_at: new Date().toISOString(),
      active: true,
      user_id: currentUser?.user_id
    };
    setPurchases(prev => [...prev, newPurchase]);
    addActivity({
      type: 'Kjøp',
      title: `Kjøpte ${item.title}`,
      status: 'Gjennomført',
      date: new Date().toISOString()
    });
  };

  const installMarketplacePlaybook = (projectId, mpiId) => {
    const template = partnerPlaybooks.find(p => p.marketplace_item_id === mpiId);
    if (template) {
      const newScenarios = (template.template_scenarios || []).map(s => ({
        ...s,
        id: generateId('sc'),
        project_id: projectId,
        source: `Marketplace: ${template.name}`,
        visibility: 'Team',
        created_at: new Date().toISOString()
      }));
      setScenarios(prev => [...prev, ...newScenarios]);
    }
  };

  const getPlaybook = (projectId) => {
    let pb = playbooks.find(p => p.project_id === projectId);
    if (!pb) {
      pb = { id: generateId('pb'), project_id: projectId, status: 'Aktiv' };
      setPlaybooks(prev => [...prev, pb]);
    }
    return pb;
  };

  const updatePlaybook = (projectId, updates) => {
    setPlaybooks(prev => prev.map(p => p.project_id === projectId ? { ...p, ...updates } : p));
  };

  const createPartnerPlaybook = (data) => {
    const newPPB = { ppb_id: generateId('ppb'), created_at: new Date().toISOString(), locked: true, template_scenarios: [], ...data };
    setPartnerPlaybooks(prev => [...prev, newPPB]);
    return newPPB;
  };

  const updatePartnerPlaybook = (ppbId, updates) => {
    setPartnerPlaybooks(prev => prev.map(p => p.ppb_id === ppbId ? { ...p, ...updates } : p));
  };

  const applyPartnerPlaybook = (projectId, ppbId) => {
    const template = partnerPlaybooks.find(p => p.ppb_id === ppbId);
    if (!template) return;
    const newScenarios = (template.template_scenarios || []).map(s => ({
      ...s,
      id: generateId('sc'),
      project_id: projectId,
      source: `Partner template: ${template.name}`,
      visibility: 'Team',
      created_at: new Date().toISOString()
    }));
    setScenarios(prev => [...prev, ...newScenarios]);
  };

  const createStandardOnboardingPack = (projectId) => {
    const newTasks = [
        { title: "Mål og suksess", description: "Definer suksess.", owner: "Kunde" },
        { title: "Teknisk oppsett", description: "Konfigurer miljø.", owner: "Team" }
    ].map(t => ({ id: generateId('ob'), project_id: projectId, ...t, status: 'Ikke startet', visibility: 'Kunde', submission: '' }));
    setOnboardingTasks(prev => [...prev, ...newTasks]);
  };

  const updateOnboardingTask = (taskId, updates) => {
    setOnboardingTasks(prev => prev.map(t => t.id === taskId ? { ...t, ...updates } : t));
  };

  const createIndustryScenarioPack = (projectId, industry) => {
    let newScenarios = [];
    
    if (industry === 'HMS' || industry === 'Compliance') {
      newScenarios = [
        {
          name: "Risikoavklaring (Compliance)",
          category: "Discovery",
          goal: "Avdekk gap mellom lovkrav og dagens praksis.",
          checklist: "- Spør om siste revisjon\n- Kartlegg manuelle prosesser\n- Identifiser personlig ansvar (styre/ledelse)",
          recommended_questions: "Hva skjer hvis Arbeidstilsynet kommer i morgen?",
          source: "Compliance Sales Kit"
        },
        {
          name: "ROI vs Risiko Presentasjon",
          category: "Demo",
          goal: "Vis at kostnaden ved avvik er høyere enn løsningen.",
          checklist: "- Bruk ROI-kalkulatoren\n- Vis avviksmodulen\n- Referer til lovparagrafer",
          recommended_questions: "Er dere villige til å ta risikoen ved å gjøre ingenting?",
          source: "Compliance Sales Kit"
        }
      ];
    } else {
      // Standard
      newScenarios = [
        { name: "Første kontakt", category: "Første kontakt", goal: "Skape interesse." },
        { name: "Discovery", category: "Discovery", goal: "Forstå behov." }
      ];
    }

    const mapped = newScenarios.map(s => ({ 
      ...s, 
      id: generateId('sc'), 
      project_id: projectId, 
      visibility: 'Team',
      created_at: new Date().toISOString()
    }));
    
    setScenarios(prev => [...prev, ...mapped]);
    addActivity({
      type: 'Oppgave',
      title: `${industry} Scenario Pack opprettet`,
      description: `La til ${mapped.length} scenarioer i prosjektet.`,
      project_id: projectId,
      status: 'Gjennomført',
      date: new Date().toISOString()
    });
  };

  const createStandardBattlecardPack = (projectId) => {
    const cards = [{ competitor_name: "Generisk", strengths: "Pris", weaknesses: "Funksjon" }]
      .map(b => ({ ...b, id: generateId('bc'), project_id: projectId, visibility: 'Team' }));
    setBattlecards(prev => [...prev, ...cards]);
  };

  const updateBattlecard = (cardId, updates) => {
    setBattlecards(prev => prev.map(c => c.id === cardId ? { ...c, ...updates } : c));
  };

  const runAiNextBestAction = async (projectId) => {
    const suggestion = await mockAI.suggestNextActions({ title: 'Analyse' }, 'General');
    const newEvent = { id: generateId('ce'), project_id: projectId, event_type: 'NextBestAction', message: suggestion, created_at: new Date().toISOString() };
    setCoachEvents(prev => [newEvent, ...prev]);
  };

  const runAiPositioning = async (projectId) => {
    const pitch = "Vår løsning hjelper...";
    updatePlaybook(projectId, { positioning: { elevator_pitch: pitch } });
    return pitch;
  };

  const logScenarioUsage = (usageData) => {
    const newUsage = { usage_id: generateId('usage'), used_by_user_id: currentUser?.user_id, used_at: new Date().toISOString(), ...usageData };
    setScenarioUsage(prev => [newUsage, ...prev]);
  };

  // --- EXPORT ---
  return (
    <PlaybookContext.Provider value={{
      playbooks, partnerPlaybooks, marketplaceItems, purchases, battlecards, scenarios, onboardingTasks, coachEvents, aiPolicies, scenarioUsage, governanceReports, learningModules,
      getPlaybook, updatePlaybook, createPartnerPlaybook, updatePartnerPlaybook, applyPartnerPlaybook, 
      purchaseMarketplaceItem, installMarketplacePlaybook, createStandardOnboardingPack, createStandardScenarioPack: (pid) => createIndustryScenarioPack(pid, 'Standard'),
      createIndustryScenarioPack, createStandardBattlecardPack, updateOnboardingTask, updateBattlecard, 
      runAiNextBestAction, runAiPositioning, logScenarioUsage, generateGovernanceReport, createLearningModule
    }}>
      {children}
    </PlaybookContext.Provider>
  );
};